import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Upload, FileSearch, X, Printer, Download, Loader2, ShieldCheck, ShieldAlert, ShieldX } from "lucide-react";

const RISK_STYLE = {
  Low:      { icon: ShieldCheck, cls: "bg-green-50 text-green-700 border-green-200" },
  Moderate: { icon: ShieldAlert, cls: "bg-amber-50 text-amber-700 border-amber-200" },
  High:     { icon: ShieldX,     cls: "bg-red-50 text-red-700 border-red-200" },
};
const SEV_CLS = {
  Critical: "bg-red-100 text-red-800 border-red-300",
  High: "bg-orange-100 text-orange-700 border-orange-200",
  Medium: "bg-amber-50 text-amber-700 border-amber-200",
  Low: "bg-slate-50 text-slate-600 border-slate-200",
};

export default function ContractEvaluatorLauncher() {
  const [modal, setModal] = useState(null);   // "picker" | null
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (!file) return toast.error("Please choose a PDF / DOCX file");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/evaluate/upload", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000 });
      setResult(data);
      setModal(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Evaluation failed");
    } finally { setBusy(false); }
  };

  return (
    <>
      <button onClick={() => setModal("picker")} data-testid="evaluator-tile"
              className="text-left bg-gradient-to-br from-[#005289] to-[#003a63] text-white rounded-lg p-5 shadow-sm hover:shadow-md transition w-full">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="w-5 h-5 text-amber-300"/>
          <div className="text-xs uppercase tracking-wider font-bold text-blue-100">AI · Contract Evaluator</div>
        </div>
        <div className="text-lg font-bold mb-1">Evaluate a contract</div>
        <div className="text-xs text-blue-100 leading-relaxed">
          Contract Evaluator uses AI to review a contract, summarize its key terms, and flag provisions that are favorable or
          unfavorable to the Health Care District based on standards set by the Office of General Counsel. Results are
          preliminary and are reviewed by the Legal Department before approval.
        </div>
        <div className="mt-3 inline-flex items-center gap-1 text-xs font-semibold bg-white/10 rounded px-2 py-1">
          <FileSearch className="w-3 h-3"/> Start evaluation →
        </div>
      </button>

      {modal === "picker" ? (
        <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center" onClick={() => setModal(null)} data-testid="evaluator-picker">
          <div className="bg-white rounded-md w-[520px] max-w-[92vw]" onClick={e => e.stopPropagation()}>
            <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#005289]"/>
              <div className="font-bold">Contract Evaluator</div>
              <button onClick={() => setModal(null)} className="ml-auto text-slate-400"><X className="w-4 h-4"/></button>
            </div>
            <div className="p-5 space-y-3">
              <div className="text-xs text-slate-500">Upload a contract (PDF or DOCX). The evaluator will summarize its key terms and flag items against the active rule library.</div>
              <label className="block">
                <input type="file" accept=".pdf,.docx" onChange={e => setFile(e.target.files?.[0] || null)}
                       data-testid="evaluator-file" className="text-xs"/>
                <div className="text-[11px] text-slate-400 mt-1">{file ? `Selected: ${file.name}` : "No file chosen"}</div>
              </label>
              <div className="bg-amber-50 border border-amber-200 rounded p-2 text-[11px] text-amber-800">
                AI-generated preliminary evaluation. Not legal advice. Subject to review by the Office of General Counsel.
              </div>
            </div>
            <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
              <button onClick={() => setModal(null)} className="h-9 px-3 text-xs border border-slate-200 rounded">Cancel</button>
              <button onClick={run} disabled={busy || !file} data-testid="evaluator-run" className="h-9 px-4 rounded text-xs font-semibold bg-[#005289] text-white flex items-center gap-1 disabled:opacity-60">
                {busy ? <Loader2 className="w-3 h-3 animate-spin"/> : <Upload className="w-3 h-3"/>} {busy ? "Evaluating…" : "Evaluate"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {result ? <ResultModal r={result} onClose={() => setResult(null)}/> : null}
    </>
  );
}

export function ResultModal({ r, onClose }) {
  const res = r.result || {};
  const RiskIcon = (RISK_STYLE[res.overall_risk] || RISK_STYLE.Moderate).icon;
  const riskCls = (RISK_STYLE[res.overall_risk] || RISK_STYLE.Moderate).cls;
  const snap = r.contract_snapshot || {};

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="evaluator-result">
      <div className="bg-white rounded-md w-[900px] max-w-[95vw] my-8" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-200 flex items-start gap-3">
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Contract Evaluation</div>
            <div className="font-bold text-lg text-slate-900">{snap.name || snap.file_name || "Uploaded contract"}</div>
            <div className="text-xs text-slate-500 mt-1">
              {snap.vendor_name ? <>Vendor: <span className="font-semibold text-slate-700">{snap.vendor_name}</span> · </> : null}
              {snap.contract_value ? <>Value: <span className="font-semibold">${(snap.contract_value || 0).toLocaleString()}</span> · </> : null}
              {snap.term ? <>Term: <span className="font-semibold">{snap.term}</span> · </> : null}
              Evaluated: {new Date(r.created_at).toLocaleString()}
            </div>
          </div>
          <div className={`text-center border rounded-md p-3 ${riskCls}`}>
            <RiskIcon className="w-5 h-5 mx-auto mb-1"/>
            <div className="text-[10px] uppercase font-bold">Overall Risk</div>
            <div className="text-xl font-bold">{res.overall_risk || "—"}</div>
            <div className="text-[10px]">Score {res.risk_score ?? 0}/100</div>
          </div>
          <button onClick={onClose} className="text-slate-400"><X className="w-5 h-5"/></button>
        </div>

        <div className="p-5 space-y-5 max-h-[70vh] overflow-y-auto">
          <div className="bg-amber-50 border border-amber-200 rounded p-2 text-[11px] text-amber-800">
            AI-generated preliminary evaluation. Not legal advice. Subject to review by the Office of General Counsel.
          </div>

          <section>
            <h3 className="text-xs font-bold uppercase tracking-wider text-[#005289] mb-2">Executive Summary</h3>
            <p className="text-sm text-slate-700 whitespace-pre-wrap">{res.executive_summary || "—"}</p>
          </section>

          {res.key_terms ? (
            <section>
              <h3 className="text-xs font-bold uppercase tracking-wider text-[#005289] mb-2">Key Terms</h3>
              <div className="grid grid-cols-2 gap-3 text-xs">
                {Object.entries(res.key_terms).map(([k, v]) => v ? (
                  <div key={k} className="bg-slate-50 border border-slate-200 rounded p-2">
                    <div className="text-[10px] uppercase text-slate-500 font-bold">{k.replace(/_/g, " ")}</div>
                    <div className="text-slate-800 mt-0.5">{String(v)}</div>
                  </div>
                ) : null)}
              </div>
            </section>
          ) : null}

          <FindingsSection title="✓ Favorable" items={res.favorable} tone="green" showRedline={false}/>
          <FindingsSection title="⚠ Unfavorable" items={res.unfavorable} tone="red" showSeverity showRedline/>
          <FindingsSection title="? Needs Review" items={res.needs_review} tone="amber" simple/>
          <FindingsSection title="✗ Missing Required Clauses" items={res.missing_required} tone="blue" simple/>
        </div>

        <div className="px-5 py-3 border-t border-slate-200 flex items-center gap-2">
          <span className="text-[10px] text-slate-400">Rule library v{r.rule_library_version || 0} · Evaluation ID {r.id}</span>
          <button onClick={() => window.print()} className="ml-auto h-9 px-3 text-xs border border-slate-200 rounded flex items-center gap-1"><Printer className="w-3 h-3"/> Print</button>
          <button onClick={() => downloadJSON(r)} className="h-9 px-3 text-xs border border-slate-200 rounded flex items-center gap-1"><Download className="w-3 h-3"/> Download</button>
          <button onClick={onClose} className="h-9 px-4 rounded text-xs font-semibold bg-[#005289] text-white">Close</button>
        </div>
      </div>
    </div>
  );
}

function FindingsSection({ title, items, tone, showSeverity, showRedline, simple }) {
  if (!items || !items.length) return null;
  const toneCls = tone === "green" ? "border-green-200 bg-green-50/40"
               : tone === "red"   ? "border-red-200 bg-red-50/40"
               : tone === "amber" ? "border-amber-200 bg-amber-50/40"
               : "border-blue-200 bg-blue-50/40";
  return (
    <section>
      <h3 className="text-xs font-bold uppercase tracking-wider text-[#005289] mb-2">{title} ({items.length})</h3>
      <div className="space-y-2">
        {items.map((it, i) => (
          <div key={i} className={`border rounded p-3 text-xs ${toneCls}`}>
            <div className="flex items-start gap-2 mb-1">
              <div className="font-semibold text-slate-900 flex-1">{it.rule_name || it.rule_id}</div>
              {showSeverity && it.severity ? (
                <span className={`text-[10px] px-2 py-0.5 border rounded ${SEV_CLS[it.severity] || ""}`}>{it.severity}</span>
              ) : null}
            </div>
            {simple ? (
              <div className="text-slate-600">{it.reason || it.explanation}</div>
            ) : (
              <>
                {it.section ? <div className="text-[10px] text-slate-500">Section: {it.section}</div> : null}
                {it.quote ? <div className="mt-1 italic text-slate-700 border-l-2 border-slate-300 pl-2">"{it.quote}"</div> : null}
                {it.explanation ? <div className="mt-1 text-slate-700">{it.explanation}</div> : null}
                {showRedline && it.suggested_redline ? (
                  <div className="mt-2 p-2 bg-white border border-slate-200 rounded">
                    <div className="text-[10px] uppercase text-slate-500 font-bold mb-1">Suggested redline</div>
                    <div className="text-slate-700">{it.suggested_redline}</div>
                  </div>
                ) : null}
              </>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

function downloadJSON(r) {
  const blob = new Blob([JSON.stringify(r, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `evaluation-${r.id}.json`;
  a.click();
  URL.revokeObjectURL(url);
}
